import { Injectable } from '@angular/core';
import { EntityState, EntityStore, StoreConfig } from '@datorama/akita';
import { of } from 'rxjs';

import { DocumentRepositoryPort } from 'document/port/document-repository.port';
import { ModuleQuestionRepositoryPort } from 'module/page/question/port/module-question-repository.port';
import { UserModuleAnswerType } from 'user/model/module/answer/user-module-answer.type';
import { UserModuleAnswer } from 'user/user-module-answer';
import { UserModuleAnswerTask } from 'user/user-module-answer-task';

import { AppLocator } from '../../../app.locator';

export interface UserModuleAnswerTaskDocument {
	readonly id: string;
	readonly label: string;
	readonly userTaskId: string;
	readonly dueTimestamp: number;
}

export interface UserAnswerDocument {
	readonly id: string;
	readonly type: string;
	readonly value: string;
	readonly userId: string;
	readonly pageId: string;
	readonly moduleId: string;
	readonly contactId: string;
	readonly articleId: string;
	readonly documentId: string;
	readonly questionId: string;
	readonly orgModuleId: string;
	readonly questionName: string;
	readonly reportTaskId: string;
	readonly updateTimestamp: number;
	readonly reportJobOfferId: string;
	readonly task: UserModuleAnswerTaskDocument;
}

export type UserAnswerState = EntityState<UserAnswerDocument, string>;

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'userAnswers' })
export class UserAnswerStore extends EntityStore<UserAnswerState> {
	constructor() {
		super();
	}

	public asDocument(answer: UserModuleAnswer): UserAnswerDocument {
		return answer
			? {
					id: answer.id,
					userId: answer.userId,
					documentId: answer.documentId,
					orgModuleId: answer.orgModuleId,
					moduleId: answer.moduleId,
					articleId: answer.articleId,
					contactId: answer.contactId,
					pageId: answer.pageId,
					type: answer.type,
					value: answer.value,
					reportTaskId: answer.reportTaskId,
					updateTimestamp: answer.updateTimestamp,
					reportJobOfferId: answer.reportJobOfferId,
					questionId: answer.questionId,
					questionName: answer.questionName,
					task: answer.task
						? {
								id: answer.task.id,
								dueTimestamp: answer.task.dueDate?.getTime(),
								label: answer.task.label,
								userTaskId: answer.task.userTaskId,
						  }
						: null,
			  }
			: null;
	}

	public asEntity(document: UserAnswerDocument): UserModuleAnswer {
		const documentRepositoryPort = AppLocator.inject<DocumentRepositoryPort>(
			DocumentRepositoryPort
		);
		const moduleQuestionRepositoryPort =
			AppLocator.inject<ModuleQuestionRepositoryPort>(
				ModuleQuestionRepositoryPort
			);

		return document
			? new UserModuleAnswer(
					document.id,
					document.value,
					document.userId,
					document.pageId,
					document.moduleId,
					document.contactId,
					document.articleId,
					document.documentId,
					document.questionId,
					document.orgModuleId,
					document.questionName,
					document.reportTaskId,
					document.updateTimestamp,
					document.reportJobOfferId,
					UserModuleAnswerType[document.type],
					document.task
						? new UserModuleAnswerTask(
								document.task.id,
								document.task.label,
								document.task.dueTimestamp
									? new Date(document.task.dueTimestamp)
									: null,
								document.task.userTaskId
						  )
						: null,
					document.documentId
						? documentRepositoryPort.observeDocument(document.documentId)
						: of(null),
					document.questionId
						? moduleQuestionRepositoryPort.observeModuleQuestionById(
								document.questionId
						  )
						: of(null)
			  )
			: null;
	}
}
